/**
 * PreOpReadinessStrip.js
 *
 * Compact horizontal readiness bar shown at the top of the Pre-Op
 * section. Shows status chips for assessment, checklist, and consent.
 */
import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { surgeryService } from '../../../services/surgeryService';

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '10px 16px',
    borderRadius: 6,
    border: '1px solid #EEEEEE',
    backgroundColor: '#FAFAFA',
    marginBottom: 16,
    flexWrap: 'wrap',
  },
  label: {
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#9E9E9E',
    marginRight: 4,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: '0.72rem',
    fontWeight: 600,
  },
  done: {
    backgroundColor: '#E8F5E9',
    color: '#2E7D32',
  },
  pending: {
    backgroundColor: '#FFF8E1',
    color: '#F57F17',
  },
  spacer: {
    flex: 1,
  },
  overall: {
    fontSize: '0.78rem',
    fontWeight: 600,
  },
  overallReady: {
    color: '#2E7D32',
  },
  overallNotReady: {
    color: '#F57F17',
  },
}));

const Dot = ({ done }) => (
  <span style={{
    width: 6,
    height: 6,
    borderRadius: '50%',
    backgroundColor: done ? '#4CAF50' : '#FF9800',
    display: 'inline-block',
  }} />
);

const PreOpReadinessStrip = ({ surgeryId }) => {
  const classes = useStyles();
  const [status, setStatus] = useState({ assessment: false, checklist: false, consent: false });

  useEffect(() => {
    if (!surgeryId) return;
    surgeryService.getPreOpReadiness(surgeryId).then((res) => {
      const data = res?.data || res || {};
      setStatus({
        assessment: !!data.assessment,
        checklist: !!data.checklist,
        consent: !!data.consent,
      });
    }).catch(() => {});
  }, [surgeryId]);

  const allDone = status.assessment && status.checklist && status.consent;

  return (
    <div className={classes.root}>
      <span className={classes.label}>Readiness</span>

      <span className={`${classes.chip} ${status.assessment ? classes.done : classes.pending}`}>
        <Dot done={status.assessment} />
        Assessment
      </span>
      <span className={`${classes.chip} ${status.checklist ? classes.done : classes.pending}`}>
        <Dot done={status.checklist} />
        Checklist
      </span>
      <span className={`${classes.chip} ${status.consent ? classes.done : classes.pending}`}>
        <Dot done={status.consent} />
        Consent
      </span>

      <span className={classes.spacer} />

      <span className={`${classes.overall} ${allDone ? classes.overallReady : classes.overallNotReady}`}>
        {allDone ? 'Ready' : 'Pending'}
      </span>
    </div>
  );
};

export default PreOpReadinessStrip;
